import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  MenuItem,
  Typography,
} from '@material-ui/core'
import React from 'react'
import { ChevronDown } from 'react-feather'
import { Control, FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  areaUnit,
  powerPer1000Unit,
  powerUnit,
  temperatureKelvinUnit,
  temperatureUnit,
} from '../../../../util/formatters'
import { validators } from '../../../../validation/validators'
import { ControlledTextField } from '../../../form/ControlledTextField'
import { FormGroup } from '../../../form/FormGroup'
import { FormValues } from '../form-values'
import { theme } from '../../../../theme'
import { ScheduleFragment } from '../../../../generated/graphql'

type Props = {
  schedules: ScheduleFragment[]
  control: Control<FormValues>
  errors: FieldErrors<FormValues>
}

export function CoolingForm({
  schedules,
  control,
  errors,
}: Props): React.ReactElement {
  const { t } = useTranslation()
  return (
    <FormGroup width="100%">
      <Accordion elevation={3}>
        <AccordionSummary
          expandIcon={<ChevronDown id="collection-cooling-expand-button" />}
        >
          <Box display="flex" alignItems="center">
            <Typography>
              {t('energySystem.parameters.cooling.tabs.systemCooling')}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <ControlledTextField
                control={control}
                name="parameters.cooling.systemCoolingPower"
                error={errors.parameters?.cooling?.systemCoolingPower}
                label={t('energySystem.parameters.cooling.systemCoolingPower')}
                rules={{ ...validators.required, ...validators.decimal }}
                InputProps={{
                  endAdornment: <Typography>{powerUnit}</Typography>,
                }}
              />
            </Grid>

            <Grid item xs={8}>
              <ControlledTextField
                control={control}
                name="scheduleId"
                error={errors.scheduleId}
                label={t('energySystem.parameters.cooling.usageProfile')}
                rules={validators.required}
                select
              >
                {Object.values(schedules).map((schedule) => (
                  <MenuItem key={schedule.id} value={schedule.id}>
                    {schedule.name}
                  </MenuItem>
                ))}
              </ControlledTextField>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Accordion style={{ marginTop: theme.spacing(1) }} elevation={3}>
        <AccordionSummary
          expandIcon={<ChevronDown id="collection-cooling-expand-button" />}
        >
          <Box display="flex" alignItems="center">
            <Typography>
              {t('energySystem.parameters.cooling.tabs.spaceCooling')}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container key="spaceCooling" spacing={2}>
            <Grid item xs={6}>
              <ControlledTextField
                control={control}
                name="parameters.cooling.scStartTemp"
                error={errors.parameters?.cooling?.scStartTemp}
                label={t('energySystem.parameters.cooling.scStartTemp')}
                rules={{ ...validators.required, ...validators.decimal }}
                InputProps={{
                  endAdornment: <Typography>{temperatureUnit}</Typography>,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <ControlledTextField
                control={control}
                name="parameters.cooling.scIncreaseSlope"
                error={errors.parameters?.cooling?.scIncreaseSlope}
                label={t('energySystem.parameters.cooling.scIncreaseSlope')}
                rules={{ ...validators.required, ...validators.decimal }}
                InputProps={{
                  endAdornment: (
                    <Typography>
                      {powerPer1000Unit}/{areaUnit}/{temperatureKelvinUnit}
                    </Typography>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <ControlledTextField
                control={control}
                name="parameters.cooling.scIncreaseExtraCoef"
                error={errors.parameters?.cooling?.scIncreaseExtraCoef}
                label={t('energySystem.parameters.cooling.scIncreaseExtraCoef')}
                rules={{ ...validators.required, ...validators.decimal }}
              />
            </Grid>
            <Grid item xs={6}>
              <ControlledTextField
                control={control}
                name="parameters.cooling.scExtraCoefStartTemp"
                error={errors.parameters?.cooling?.scExtraCoefStartTemp}
                label={t(
                  'energySystem.parameters.cooling.scExtraCoefStartTemp',
                )}
                rules={{ ...validators.required, ...validators.decimal }}
                InputProps={{
                  endAdornment: <Typography>{temperatureUnit}</Typography>,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <ControlledTextField
                control={control}
                name="parameters.cooling.scDistributionEfficiency"
                error={errors.parameters?.cooling?.scDistributionEfficiency}
                label={t(
                  'energySystem.parameters.cooling.scDistributionEfficiency',
                )}
                rules={{ ...validators.required, ...validators.decimal }}
              />
            </Grid>
            <Grid item xs={6}>
              <ControlledTextField
                control={control}
                name="parameters.cooling.scSpacesPortion"
                error={errors.parameters?.cooling?.scSpacesPortion}
                label={t('energySystem.parameters.cooling.scSpacesPortion')}
                rules={{ ...validators.required, ...validators.decimal }}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Accordion style={{ marginTop: theme.spacing(1) }} elevation={3}>
        <AccordionSummary
          expandIcon={<ChevronDown id="collection-cooling-expand-button" />}
        >
          <Box display="flex" alignItems="center">
            <Typography>
              {t('energySystem.parameters.cooling.tabs.ahuCooling')}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container key="ahuCooling" spacing={2}>
            <Grid item xs={6}>
              <ControlledTextField
                control={control}
                name="parameters.cooling.acDemandDisplacementConstant"
                error={errors.parameters?.cooling?.acDemandDisplacementConstant}
                label={t(
                  'energySystem.parameters.cooling.acDemandDisplacementConstant',
                )}
                rules={{ ...validators.required, ...validators.decimal }}
                InputProps={{
                  endAdornment: (
                    <Typography>{temperatureKelvinUnit}</Typography>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <ControlledTextField
                control={control}
                name="parameters.cooling.acCorrectionCoef"
                error={errors.parameters?.cooling?.acCorrectionCoef}
                label={t('energySystem.parameters.cooling.acCorrectionCoef')}
                rules={{ ...validators.required, ...validators.decimal }}
              />
            </Grid>
            <Grid item xs={4}>
              <ControlledTextField
                control={control}
                name="parameters.cooling.acIncreaseExtraCoef"
                error={errors.parameters?.cooling?.acIncreaseExtraCoef}
                label={t('energySystem.parameters.cooling.acIncreaseExtraCoef')}
                rules={{ ...validators.required, ...validators.decimal }}
              />
            </Grid>
            <Grid item xs={4}>
              <ControlledTextField
                control={control}
                name="parameters.cooling.acExtraCoefStartTemp"
                error={errors.parameters?.cooling?.acExtraCoefStartTemp}
                label={t(
                  'energySystem.parameters.cooling.acExtraCoefStartTemp',
                )}
                rules={{ ...validators.required, ...validators.decimal }}
                InputProps={{
                  endAdornment: <Typography>{temperatureUnit}</Typography>,
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <ControlledTextField
                control={control}
                name="parameters.cooling.acDistributionEfficiency"
                error={errors.parameters?.cooling?.acDistributionEfficiency}
                label={t(
                  'energySystem.parameters.cooling.acDistributionEfficiency',
                )}
                rules={{ ...validators.required, ...validators.decimal }}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </FormGroup>
  )
}
